import { DialogComponent } from '@theme/dialog';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {ResizeObserver | null} */
  #headerResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#setupHeaderHeightObserver);
    this.addEventListener(DialogCloseEvent.eventName, this.#teardownHeaderHeightObserver);
  }

  disconnectedCallback() {
    this.#teardownHeaderHeightObserver();
    this.removeEventListener(DialogOpenEvent.eventName, this.#setupHeaderHeightObserver);
    this.removeEventListener(DialogCloseEvent.eventName, this.#teardownHeaderHeightObserver);
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #setHeaderHeight = () => {
    const dialog = this.refs?.dialog;
    if (!dialog) return;
    const header = /** @type {HTMLElement | null} */ (dialog.querySelector('#cart-drawer-header'));
    const content = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-cart-drawer-content]'));
    if (header && content) {
      content.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }
  };

  #setupHeaderHeightObserver = () => {
    const dialog = this.refs?.dialog;
    if (!dialog) return;
    const header = /** @type {HTMLElement | null} */ (dialog.querySelector('#cart-drawer-header'));
    const content = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-cart-drawer-content]'));
    if (!header || !content) return;
    this.#setHeaderHeight();
    this.#teardownHeaderHeightObserver();
    this.#headerResizeObserver = new ResizeObserver(this.#setHeaderHeight);
    this.#headerResizeObserver.observe(header);
  };

  #teardownHeaderHeightObserver = () => {
    if (this.#headerResizeObserver) {
      this.#headerResizeObserver.disconnect();
      this.#headerResizeObserver = null;
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
